import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TaskItem } from '@demind-inc/core';
import {
  DragIndicator,
  EditOutlined,
  Check,
  Delete,
  RemoveCircle,
  Remove,
  BorderColor,
} from '@mui/icons-material';
import clsx from 'clsx';

import './SubTaskItem.scss';
import { TaskCheckBox } from '../../../common';
import { getCssVariable } from '../../../../utils';
import { SubTaskMoreDropdown, SubTaskMoreDropdownOption } from '../SubTaskMoreDropdown';
import { useDropdownState } from '../../../../hooks';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

interface SubTaskItemProps {
  task: TaskItem;
  onSubTaskComplete?: (taskId: string, completed: boolean) => void;
  onUpdateSubTask?: (taskId: string, updates: Partial<TaskItem>) => void;
  onDeleteSubTask?: (taskId: string) => void;
  onCancel?: () => void;
  isNewTask?: boolean;
  isEditing?: boolean;
  canEdit?: boolean;
  className?: string;
  isUpdating?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, taskId: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  dragOverPosition?: 'before' | 'after' | null;
  disableDrag?: boolean;
}

const taskIncompleteColor = getCssVariable('--color-gray-base');
const taskCompleteColor = getCssVariable('--color-primary');

const SubTaskItem: React.FC<SubTaskItemProps> = ({
  task,
  onSubTaskComplete = () => {},
  onUpdateSubTask = () => {},
  onDeleteSubTask = () => {},
  onCancel,
  isNewTask = false,
  isEditing = false,
  canEdit = true,
  className,
  isUpdating = false,
  onDragStart = () => {},
  onDragEnd = () => {},
  isDragging = false,
  dragOverPosition = null,
  disableDrag = false,
}) => {
  const { t } = useTranslation();
  const [isEditingLocal, setIsEditingLocal] = useState(isEditing);
  const [editName, setEditName] = useState(task.name);
  const [isSaving, setIsSaving] = useState(false);
  const { anchorEl, handleCloseDropdown, handleOpenDropdown } = useDropdownState();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing || isEditingLocal) {
      inputRef.current?.focus();
    }
  }, [isEditing, isEditingLocal, isNewTask]);

  // Reset editName when task name changes (when not editing and not saving)
  useEffect(() => {
    if (!isEditingLocal && !isEditing && !isSaving && !isUpdating) {
      setEditName(task.name);
    }
  }, [task.name, isEditingLocal, isEditing, isSaving, isUpdating]);

  const handleCancel = useCallback(() => {
    setEditName(task.name);
    setIsEditingLocal(false);
  }, [task.name]);

  // Handle Escape key to cancel editing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditingLocal && !isNewTask) {
        handleCancel();
      }
    };

    if (isEditingLocal && !isNewTask) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isEditingLocal, isNewTask, handleCancel]);

  const handleSaveEdit = useCallback(
    debounce((name: string, comparedName: string) => {
      if (name === comparedName) {
        return;
      }

      onUpdateSubTask(task.taskId, { name });
      setIsEditingLocal(false);
    }, 2000),
    []
  );

  const handleDropdownItemClick = async (option: SubTaskMoreDropdownOption) => {
    if (option === 'delete') {
      onDeleteSubTask(task.taskId);
    }
    handleCloseDropdown();
  };

  const handleNameChange = (value: string) => {
    setEditName(value);
    // Disable auto-save for all tasks - user must click check icon
    return;
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleBlur = () => {
    // Don't handle blur if we're in the process of deleting
    if (isDeleting) {
      return;
    }
    // For new tasks, only cancel if empty - otherwise user must click check or delete
    if (isNewTask) {
      if (!editName.trim() && onCancel) {
        onCancel();
      }
      return;
    }
    // For existing tasks, cancel editing on blur if no changes
    if (!isNewTask && isEditingLocal) {
      if (editName.trim() === task.name) {
        setIsEditingLocal(false);
        setEditName(task.name);
      }
      // Don't auto-save, user must click check icon
      return;
    }
  };

  const hasChanges = editName.trim() !== task.name;

  const handleSave = () => {
    if (isNewTask) {
      // For new tasks, create the subtask
      if (editName.trim()) {
        setIsSaving(true);
        onUpdateSubTask(task.taskId, { name: editName.trim() });
      }
    } else {
      // For existing tasks, update the subtask
      if (editName.trim() && editName !== task.name) {
        setIsSaving(true);
        onUpdateSubTask(task.taskId, { name: editName.trim() });
      }
      setIsEditingLocal(false);
    }
  };

  // Reset isSaving when update completes
  useEffect(() => {
    if (!isUpdating && isSaving) {
      setIsSaving(false);
    }
  }, [isUpdating, isSaving]);

  const handleDelete = () => {
    setIsDeleting(true);
    if (onDeleteSubTask) {
      onDeleteSubTask(task.taskId);
    }
    // Reset after a short delay in case the delete doesn't actually happen
    setTimeout(() => setIsDeleting(false), 100);
  };

  const isCurrentlyEditing = isEditing || isEditingLocal;
  const canDrag = !disableDrag && canEdit && !isNewTask && !isCurrentlyEditing;

  return (
    <>
      <div
        className={clsx(
          'subtask-item',
          {
            'subtask-item--editing': isCurrentlyEditing,
            'subtask-item--completed': task.completed,
            'subtask-item--dragging': isDragging,
          },
          className
        )}
        draggable={canDrag}
        onDragStart={(e) => {
          if (canDrag) {
            onDragStart(e, task.taskId);
          }
        }}
        onDragEnd={onDragEnd}
      >
        {!disableDrag && (
          <div
            className="subtask-item__drag-handle-wrapper"
            style={{ cursor: canDrag ? 'grab' : 'default' }}
          >
            <DragIndicator className="subtask-item__drag-handle" />
          </div>
        )}

        <TaskCheckBox
          disabled={!canEdit || isNewTask}
          isCompleted={task.completed}
          onChange={(completed) => onSubTaskComplete(task.taskId, completed)}
          size={24}
          color={task.completed ? taskCompleteColor : taskIncompleteColor}
          className="subtask-item__checkbox"
        />

        {canEdit ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                e.stopPropagation();
                if (hasChanges) {
                  handleSave();
                }
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Enable editing when clicking on the input
              if (!isCurrentlyEditing && !isNewTask) {
                setEditName(task.name);
                setIsEditingLocal(true);
              }
            }}
            readOnly={!isCurrentlyEditing}
            className={clsx('subtask-item__name-input', {
              'subtask-item__name-input--readonly': !isCurrentlyEditing,
            })}
            placeholder={t('task.subtasks.enterSubtaskName')}
            aria-label={t('task.subtasks.enterSubtaskName')}
          />
        ) : (
          <span
            className={clsx('subtask-item__name', {
              'subtask-item__name--completed': task.completed,
            })}
          >
            {task.name || t('task.subtasks.untitled')}
          </span>
        )}

        {dragOverPosition === 'before' && (
          <div className="subtask-item__drop-indicator subtask-item__drop-indicator--before" />
        )}
        {canEdit && (
          <>
            {/* Show check button only when there are changes */}
            {hasChanges && (isNewTask || isEditingLocal) && (
              <IconButton
                aria-label={t('task.subtasks.saveSubtask')}
                size="small"
                color="inherit"
                className="subtask-item__action-button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSave();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Check className="subtask-item__action-icon subtask-item__action-icon--check" />
              </IconButton>
            )}
            {/* Show loader while updating, otherwise show remove button */}
            {isUpdating ? (
              <CircularProgress size={16} className="subtask-item__loading-icon" />
            ) : (
              <IconButton
                aria-label={t('task.subtasks.deleteSubtask')}
                size="small"
                color="inherit"
                className="subtask-item__action-button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isNewTask && onCancel) {
                    onCancel();
                  } else if (!isNewTask) {
                    handleDelete();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Remove className="subtask-item__action-icon subtask-item__action-icon--delete" />
              </IconButton>
            )}
          </>
        )}
        {dragOverPosition === 'after' && (
          <div className="subtask-item__drop-indicator subtask-item__drop-indicator--after" />
        )}
      </div>

      <SubTaskMoreDropdown
        anchorEl={anchorEl}
        onClickItem={handleDropdownItemClick}
        onClose={handleCloseDropdown}
      />
    </>
  );
};

export default SubTaskItem;
